import { Meeting, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PlayerItem, PlayerRendererProvider, PlayerRendererProviderOptions } from "../interfaces";
import { getDefaultPlayerGridItemRendererProvider, PlayersGrid, PlayersGridProps, PlayersGridRef } from "../PlayersGrid";

import { Touchable, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import styles from "./MeetingPlayerTriLayoutContainer.module.css";
import { useVideoSubscriptions } from "../../../hooks/ovenmedia";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { MeetingSessionOvenMediaReceiverManagerBaseData } from "@kalyzee/kast-web-sdk";
import { ReactIcons } from "@kalyzee/kalyzee-ui";
import { LayoutType } from "../../../interfaces/ui";

export interface MeetingPlayerTriLayoutContainerProps {
  meeting: Meeting;
  sessions: MeetingSession[];
  items: PlayerItem[];
  layout: LayoutType;
  layoutProps?: { square?: { gridProps?: PlayersGridProps["gridProps"]; noPages?: boolean } };
  style?: React.CSSProperties;
  defaultRendererProviderOptions?: PlayerRendererProviderOptions;
  autoOnOffVideo?: boolean;
  renderItem?: (
    rendererProvider: PlayerRendererProvider,
    ctx: {
      item: PlayerItem, 
      isSelectedPlayer: boolean,
      onSelect?: () => void,
    },
  ) => React.ReactNode;
}

export const MeetingPlayerTriLayoutContainer = ({
  meeting,
  sessions,
  items,
  layout,
  layoutProps,
  style,
  autoOnOffVideo = true,
  defaultRendererProviderOptions,
  renderItem,
}: MeetingPlayerTriLayoutContainerProps) => {
  const [selectedItemId, setSelectedItemId] = useState<string>();

  const selectedItem = useMemo(() => {
    const _selectedItem = selectedItemId ? items.find((i) => i.getId() === selectedItemId) : undefined;
    const selectedItem = _selectedItem ?? (items.length ? items[0] : undefined);
    return selectedItem;
  }, [items, selectedItemId]);

  const unselectedItems = useMemo(() => items.filter((i) => i !== selectedItem), [items, selectedItem]);

  const autoOnOffVideoRef = useRefAutoUpdate(autoOnOffVideo);
  const subscribptionsAPI = useVideoSubscriptions();
  const currentLayoutRef = useRef(layout);
  const gridRef = useRef<PlayersGridRef>(null);

  // ---------------------------------------------------------------------- //
  // ---------------------------  UTILS ----------------------------- //
  // ---------------------------------------------------------------------- //

  const updateVideo = useCallback((
    session: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>,
    forceValue?: boolean,
  ) => {
    if (!autoOnOffVideoRef.current) return;

    let shouldBeEnabled;
    if (forceValue !== undefined) {
      shouldBeEnabled = forceValue;
    } else {
      if (currentLayoutRef.current === LayoutType.Square) {
        shouldBeEnabled = gridRef.current?.currentlyRendering(session) ?? false;
      } else if (currentLayoutRef.current === LayoutType.HorizontalSquare) {
        shouldBeEnabled = true;
      } else if (currentLayoutRef.current === LayoutType.VerticalSquare) {
        shouldBeEnabled = true;
      } else {
        shouldBeEnabled = false;
      }
    }

    subscribptionsAPI.update([{ session, active: shouldBeEnabled }]);
  }, [subscribptionsAPI]);

  const updateVideos = useCallback((forceValue?: boolean) => {
    const sessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData>[] = [];
    items.forEach((item) => {
      const data = item.getData();
      if (data.type === "meeting_session") {
        sessions.push(...data.videoSessions);
      } else if (data.type === "video_session") {
        sessions.push(data.videoSession);
      }
    })
    sessions.forEach((s) => updateVideo(s, forceValue));
  }, [items, updateVideo]);

  const gridProps: PlayersGridProps["gridProps"] = useMemo(() => ({
    minColumn: 1,
    minRow: 1,
    maxColumn: 4,
    maxRow: 3,
    ...(layoutProps?.square?.gridProps ?? {}),
    callbacks: {
      change: () => {
        updateVideos();
      }
    }
  }), [layoutProps?.square?.gridProps, updateVideos]);

  const gridStyles = useMemo(() => ({ height: "100%", width: "100%", minHeight: "0px" }), []);

  const gridDefaultRendererProviderOptions: PlayerRendererProviderOptions = useMemo(() => defaultRendererProviderOptions ?? ({
    muted: true,
    hideSoundEffect: false,
  }), [defaultRendererProviderOptions]);

  // ---------------------------------------------------------------------- //
  // ---------------------------  EFFECTS ----------------------------- //
  // ---------------------------------------------------------------------- //

  useEffect(() => {
    return () => {
      updateVideos(false); // force disable videos
    };
  }, []);

    // ---------------------------------------------------------------------- //
  // ---------------------------  RENDER ----------------------------- //
  // ---------------------------------------------------------------------- //

  const _renderItem = useCallback((
    rendererProvider: PlayerRendererProvider, 
    item: PlayerItem, 
    isSelectedPlayer: boolean, 
    style?: React.CSSProperties,
  ) => {
    const onSelect = isSelectedPlayer || layout === LayoutType.Square 
      ? undefined 
      : () => item && setSelectedItemId(item.getId());
    return (
      <div
        key={`tri-layout-item-${item.getId()}`}
        style={style}
      >
        {renderItem ? renderItem(rendererProvider, { item, isSelectedPlayer, onSelect }) : rendererProvider()}
      </div>
    );
  }, [layout, renderItem, setSelectedItemId]);

  const _renderGridItem = useCallback<NonNullable<PlayersGridProps["renderItem"]>>((rendererProvider, item) => (
    item ? _renderItem(rendererProvider, item, true, { width: "100%", height: "100%" }) : <div></div>
  ), [_renderItem]);

  const renderLayoutContent = () => {
    if (!items.length) return null;
    if (layout === LayoutType.Square || !selectedItem) {
      currentLayoutRef.current = LayoutType.Square;
      return (
        <PlayersGrid
          name="TriLayout"
          style={gridStyles}
          ref={gridRef}
          meeting={meeting}
          sessions={sessions}
          items={items}
          renderItem={_renderGridItem}
          gridProps={gridProps}
          noPages={layoutProps?.square?.noPages}
          autoOnOffVideo={false}
          defaultRendererProviderOptions={gridDefaultRendererProviderOptions}
          hideButtonIfDisabled
        />
      );
    } else if (layout === LayoutType.VerticalSquare) {
      currentLayoutRef.current = LayoutType.HorizontalSquare;
      return (
        <div className={styles.horizontalLayoutContainer}>
          <div className={styles.horizontalLayoutMainPLayerContainer}>
            {selectedItem
              ? _renderItem(getDefaultPlayerGridItemRendererProvider(meeting, sessions, selectedItem, true, defaultRendererProviderOptions), selectedItem, true, {
                  height: "100%",
                  width: "100%",
                })
              : null}
          </div>
          {!unselectedItems.length || (
            <div className={styles.horizontalLayoutScrollContainer}>
              <div className={styles.horizontalLayoutScrollContainerCenteredContent}>
                {unselectedItems.map((item) =>
                  _renderItem(getDefaultPlayerGridItemRendererProvider(meeting, sessions, item, true, defaultRendererProviderOptions), item, false, {
                    maxWidth: "100%",
                    height: 165,
                    width: 220,
                  })
                )}
              </div>
            </div>
          )}
        </div>
      );
    } else if (layout === LayoutType.HorizontalSquare) {
      currentLayoutRef.current = LayoutType.VerticalSquare;
      return (
        <div className={styles.verticalLayoutContainer}>
          <div className={styles.verticalLayoutMainPLayerContainer}>
            {selectedItem
              ? _renderItem(getDefaultPlayerGridItemRendererProvider(meeting, sessions, selectedItem, true, defaultRendererProviderOptions), selectedItem, true, {
                  width: "100%",
                })
              : null}
          </div>
          {!unselectedItems.length || (
            <div className={styles.verticalLayoutScrollContainer}>
              <div className={styles.verticalLayoutScrollContainerCenteredContent}>
                {unselectedItems.map((item) =>
                  _renderItem(getDefaultPlayerGridItemRendererProvider(meeting, sessions, item, true, defaultRendererProviderOptions), item, false, {
                    maxHeight: "100%",
                    height: 165,
                    width: 220,
                  })
                )}
              </div>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return <div style={{ width: "100%", height: "100%", ...(style || {}) }}>{renderLayoutContent()}</div>;
};
